/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {error} from '@ion/core/src/logger/logger';
import {loadProvisionedDevices} from '@ion/core/src/services/Connect';
import {
  InventoryDevice
} from '@ion/core/src/types/entities';
import {InventoryDevicesFilter, ServiceError} from '@ion/core/src/types/services';
import {
  convertModularSearchDateRange
} from '@ion/core/src/utils/DateUtil';
import {
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  filterObjectByKeys
} from '@ion/core/src/utils/ObjectUtil';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  PageInitializeAction
} from '@/types/redux';
import {gridDefaultPageSize} from '../../../utils/helpers';
import {buildInventorySearch} from './helpers';

// Name of reducer
export const REDUCER_NAME                       = 'inventory';

export const INITIALIZE_INVENTORY_DEVICES       = 'INITIALIZE_INVENTORY_DEVICES';
export const SET_GRID_COLUMNS                   = 'SET_INVENTORY_DEVICES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                = 'UPDATE_INVENTORY_DEVICES_GRID_COLUMNS';
export const INVENTORY_DEVICES_GRID_REFRESHED   = 'INVENTORY_DEVICES_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                 = 'SET_INVENTORY_DEVICES_GRID_PAGE_SIZE';
export const SET_VIEW_SUB_GROUPS                = 'SET_INVENTORY_DEVICES_VIEW_SUB_GROUPS';
export const GET_INVENTORY_DEVICES_REQUEST      = 'GET_INVENTORY_DEVICES_REQUEST';
export const GET_INVENTORY_DEVICES_RESPONSE     = 'GET_INVENTORY_DEVICES_RESPONSE';
export const GET_INVENTORY_DEVICES_ERROR        = 'GET_INVENTORY_DEVICES_ERROR';
export const ACTIVATE_INVENTORY_DEVICE          = 'ACTIVATE_INVENTORY_DEVICE';
export const DELETE_INVENTORY_DEVICE            = 'DELETE_INVENTORY_DEVICE';

// Filter model ...
export const SET_INVENTORY_DEVICES_FILTER_MODEL      = 'SET_INVENTORY_DEVICES_FILTER_MODEL';
// Search queries
export const UPDATE_INVENTORY_DEVICES_SEARCH_QUERIES = 'UPDATE_INVENTORY_DEVICES_SEARCH_QUERIES';
export const RESET_INVENTORY_DEVICES_SEARCH_QUERIES  = 'RESET_INVENTORY_DEVICES_SEARCH_QUERIES';

/**
 * Initialize Inventory Devices
 */
export const initializeInventoryDevices = (): PageInitializeAction => {
  return {type: INITIALIZE_INVENTORY_DEVICES};
};

/**
 * Invoked when Grid Refreshed
 */
export const inventoryGridRefreshed = (): PageInitializeAction => {
  return {type: INVENTORY_DEVICES_GRID_REFRESHED};
};

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setInventoryViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setInventoryGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

// Updates the modular search queries for Inventory Devices
export const updateInventorySearchQueries = (searchParams: InventoryDevicesFilter) => ({
  type : UPDATE_INVENTORY_DEVICES_SEARCH_QUERIES, searchParams
});

// Resets the Inventory Devices filters state
export const resetInventorySearchQueries = () => ({
  type : RESET_INVENTORY_DEVICES_SEARCH_QUERIES
});

/**
 * get Inventory Devices
 */
export const getInventoryDevices = ({
  sort,
  region,
  callback,
  pageId = 1,
  pageSize = 20,
  includeSubGroups = false
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_INVENTORY_DEVICES_REQUEST});
    const {
      inventory: {
        search
      }
    } = getState();
    try {
      const {devices: items, totalCount} = await loadProvisionedDevices({
        pageId,
        pageSize,
        includeSubGroups,
        filterOn : buildInventorySearch({
          region,
          sort,
          searchParams : search
        })
      });
      dispatch({type: GET_INVENTORY_DEVICES_RESPONSE});
      callback?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callback?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_INVENTORY_DEVICES_ERROR, error: err});
      return err;
    }
  };
};

export enum InventorySearchFields {
  DEVICE_TYPE_NAME = 'deviceTypeName',
  SERIAL_NUMBER = 'serialNumber',
  SHIP_DATE = 'shipDate',
  SCRIPT = 'custScriptVer',
  FIRMWARE = 'firmwareVersion',
  TRACKING_NUMBER = 'trackingNumber',
  PURCHASE_ORDER = 'purchaseOrder',
  SALES_ORDER = 'salesOrder',
  REGION = 'currRegion',
  SOLOMON_ID = 'solomonId',
  IMEI = 'imei',
  IMSI = 'imsi',
  ICCID = 'iccid'
};

const clearSearch = () => ({
  deviceTypeName  : null,
  serialNumber    : null,
  custScriptVer   : null,
  firmwareVersion : null,
  shipDate        : null,
  externalData    : null
});

const externalDataFields = [
  InventorySearchFields.TRACKING_NUMBER,
  InventorySearchFields.PURCHASE_ORDER,
  InventorySearchFields.SALES_ORDER
];

/**
 * Invoked when the confirm activate device Dialog is to be displayed or removed.
 */
export const assignDevice = (device: InventoryDevice, show = true) => ({
  type : ACTIVATE_INVENTORY_DEVICE, device, show
});

/**
 * Invoked when the confirm delete device Dialog is to be displayed or removed.
 */
export const deleteDevice = (device: InventoryDevice, show = true) => ({
  type : DELETE_INVENTORY_DEVICE, device, show
});

/**
 * REDUCERS
 */

export interface InventoryDevicesAction extends AdminAction {
  searchParams?: InventoryDevicesFilter;
  device?: InventoryDevice;
  devices?: InventoryDevice[];
};

export interface InventoryDevicesActionState {
  selected: InventoryDevice | InventoryDevice[] | null;
};

export interface AssignActionState extends InventoryDevicesActionState {
  isAssigning: boolean;
}

export interface DestroyActionState extends InventoryDevicesActionState {
  isDeleting: boolean;
}

export interface InventoryDevicesState extends PageState {
  devices: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  search: InventoryDevicesFilter,
  assign: AssignActionState,
  destroy: DestroyActionState
};

// defines the user preference key for the admin devices page ...
export const PREFERENCE = 'inventory-devices';

// defines the initial state for the reducer ...
export const initialState: InventoryDevicesState = {
  isInitialized : false,
  devices       : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  search : clearSearch(),
  assign : {
    isAssigning : false,
    selected    : null
  },
  destroy : {
    isDeleting : false,
    selected   : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_INVENTORY_DEVICES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.search                   = clearSearch();
  },
  [SET_GRID_COLUMNS] : ({grid, devices}: InventoryDevicesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : devices.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: InventoryDevicesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [UPDATE_INVENTORY_DEVICES_SEARCH_QUERIES] : ({search}: InventoryDevicesState, {searchParams}) => {
    search.serialNumber    = searchParams.serialNumber || null;
    search.solomonId       = searchParams.solomonId || null;
    search.deviceTypeName  = searchParams.deviceTypeName || null;
    search.custScriptVer   = searchParams.custScriptVer || null;
    search.firmwareVersion = searchParams.firmwareVersion || null;
    search.currRegion      = searchParams.currRegion || null;
    search.imei            = searchParams.imei || null;
    search.imsi            = searchParams.imsi || null;
    search.iccid           = searchParams.iccid || null;
    search.shipDate        = searchParams?.shipDate ? `(${convertModularSearchDateRange(searchParams?.shipDate, 'YYYY-MM-DD[T]HH[\\]:mm[\\]:ss.[000Z]')})` : null;
    let parseData = null;
    if (searchParams.trackingNumber || searchParams.purchaseOrder || searchParams.salesOrder) {
      parseData = safeJSONStringify(filterObjectByKeys(searchParams, externalDataFields));
    }
    search.externalData = parseData;
  },
  [RESET_INVENTORY_DEVICES_SEARCH_QUERIES] : draft => {
    draft.search = clearSearch();
  },
  [GET_INVENTORY_DEVICES_REQUEST] : ({devices}: InventoryDevicesState) => {
    devices.loading = true;
  },
  [GET_INVENTORY_DEVICES_RESPONSE] : ({devices}: InventoryDevicesState) => {
    devices.loading = false;
  },
  [GET_INVENTORY_DEVICES_ERROR] : ({devices}: InventoryDevicesState, {error}: InventoryDevicesAction) => {
    devices.loading = false;
    devices.error   = error;
  },
  [ACTIVATE_INVENTORY_DEVICE] : ({assign}: InventoryDevicesState, {device, show}: InventoryDevicesAction) => {
    assign.selected    = show ? device : null;
    assign.isAssigning = show;
  },
  [DELETE_INVENTORY_DEVICE] : ({destroy}: InventoryDevicesState, {device, show}: InventoryDevicesAction) => {
    destroy.selected   = show ? device : null;
    destroy.isDeleting = show;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: InventoryDevicesState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: InventoryDevicesState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: InventoryDevicesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
